package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Les simulations et leurs lignes dans le moteur de synchronisation. Une ligne voyage
 * telle que tapée : son expression, jamais sa valeur, que chaque appareil recalcule.
 */
internal object SimulationSyncOps {

    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = when (entity) {
        EntityKind.Simulation -> db.simulationQueries.selectDirty()
        else -> db.simulationLineQueries.selectDirty()
    }

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Simulation -> db.simulationQueries.selectDirty().executeAsList().map { simulation(it) }
        else -> db.simulationLineQueries.selectDirty().executeAsList().map { line(it) }
    }

    fun selectAll(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Simulation -> db.simulationQueries.selectAll().executeAsList().map { simulation(it) }
        else -> db.simulationLineQueries.selectAll().executeAsList().map { line(it) }
    }

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        when (entity) {
            EntityKind.Simulation -> db.simulationQueries.markSynced(syncedAt, id, updatedAt)
            else -> db.simulationLineQueries.markSynced(syncedAt, id, updatedAt)
        }
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? = when (entity) {
        EntityKind.Simulation -> db.simulationQueries.selectByIdAnyState(id).executeAsOneOrNull()
            ?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        else -> db.simulationLineQueries.selectByIdAnyState(id).executeAsOneOrNull()
            ?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
    }

    fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Simulation -> db.simulationQueries.insert(
                row.id,
                p.str("name") ?: "",
                p.str("note"),
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            else -> db.simulationLineQueries.insert(
                row.id,
                p.str("simulation_id") ?: "",
                p.str("label") ?: "",
                p.str("expression") ?: "",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
        }
    }

    fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Simulation -> db.simulationQueries.updateFromRemote(
                p.str("name") ?: "",
                p.str("note"),
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            else -> db.simulationLineQueries.updateFromRemote(
                p.str("simulation_id") ?: "",
                p.str("label") ?: "",
                p.str("expression") ?: "",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
        }
    }

    fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow, deletedAt: Long) {
        when (entity) {
            EntityKind.Simulation -> db.simulationQueries.softDelete(deletedAt, row.updatedAt, row.id)
            else -> db.simulationLineQueries.softDelete(deletedAt, row.updatedAt, row.id)
        }
    }

    fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        when (entity) {
            EntityKind.Simulation -> db.simulationQueries.restore(row.updatedAt, row.id)
            else -> db.simulationLineQueries.restore(row.updatedAt, row.id)
        }
    }

    private fun simulation(it: com.tedd.db.Simulation) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "name" to it.name,
        "note" to it.note,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun line(it: com.tedd.db.Simulation_line) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "simulation_id" to it.simulation_id,
        "label" to it.label,
        "expression" to it.expression,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))
}
